import type { BillingCycle, PaidPlanCode, PlanEntitlement } from '../types/planTypes';

export const BILLING_CYCLES = ['monthly', 'quarterly', 'annual'] as const satisfies readonly BillingCycle[];
export const DEFAULT_BILLING_CYCLE_ORDER = ['quarterly', 'monthly', 'annual'] as const satisfies readonly BillingCycle[];
const PAID_PLANS = ['lite', 'pro', 'max'] as const satisfies readonly PaidPlanCode[];

export interface SaleablePlanPricing {
	planCode: unknown;
	provider: unknown;
	billingCycle: unknown;
	providerProductId: unknown;
	amountCents: unknown;
	currency: unknown;
	isActive: unknown;
}

/** Single server-side truth for whether a provider price may be exposed or purchased. */
export function isSaleablePlanPricing(
	price: SaleablePlanPricing,
	allowedProviders: readonly string[] = ['creem'],
): boolean {
	if (!PAID_PLANS.includes(price.planCode as PaidPlanCode)) return false;
	if (typeof price.provider !== 'string' || price.provider.length === 0) return false;
	if (!/^[\x21-\x7e]+$/u.test(price.provider)) return false;
	if (!allowedProviders.includes(price.provider)) return false;
	if (!BILLING_CYCLES.includes(price.billingCycle as BillingCycle)) return false;
	if (!(price.isActive === true || price.isActive === 1)) return false;
	if (!Number.isInteger(price.amountCents) || Number(price.amountCents) <= 0) return false;
	if (price.currency !== 'USD') return false;
	if (typeof price.providerProductId !== 'string' || price.providerProductId.length === 0) return false;
	if (!/^[\x21-\x7e]+$/u.test(price.providerProductId)) return false;
	if (/todo|placeholder/i.test(price.providerProductId)) return false;
	return true;
}

/**
 * Client plan payloads contain only saleable prices; zero on a paid tier means
 * the corresponding provider product is unavailable. A surface-wide cycle is
 * safe only when every paid tier can render and purchase it.
 */
export function getCommonPurchasableBillingCycles(plans: readonly PlanEntitlement[]): BillingCycle[] {
	const byCode = new Map(plans.map((plan) => [plan.code, plan]));
	if (PAID_PLANS.some((plan) => !byCode.has(plan))) return [];
	return BILLING_CYCLES.filter((cycle) => PAID_PLANS.every((plan) => {
		const amount = cycleAmount(byCode.get(plan)!, cycle);
		return Number.isInteger(amount) && amount > 0;
	}));
}

export function resolveDefaultPurchasableBillingCycle(plans: readonly PlanEntitlement[]): BillingCycle | null {
	const available = new Set(getCommonPurchasableBillingCycles(plans));
	return DEFAULT_BILLING_CYCLE_ORDER.find((cycle) => available.has(cycle)) ?? null;
}

export function normalizePurchasableBillingCycle(
	requested: BillingCycle,
	plans: readonly PlanEntitlement[],
): BillingCycle | null {
	const available = new Set(getCommonPurchasableBillingCycles(plans));
	if (available.has(requested)) return requested;
	return DEFAULT_BILLING_CYCLE_ORDER.find((cycle) => available.has(cycle)) ?? null;
}

export function cycleAmount(plan: PlanEntitlement, cycle: BillingCycle): number {
	if (cycle === 'monthly') return plan.monthlyCents;
	if (cycle === 'quarterly') return plan.quarterlyCents;
	return plan.annualCents;
}
